import { fetchMarketsRequest } from '@app/ducks/markets';
import { latestPageStructureVersion } from '@app/logic/cms/convertPageToLatestVersion';
import { pageFilter } from '@app/logic/cms/pagesList';
import { useCreatePage } from '@app/services/hooks/pages/useCreatePage';
import { useDuplicatePage } from '@app/services/hooks/pages/useDuplicatePage';
import { usePages } from '@app/services/hooks/pages/usePages';
import { useModalVisibility, usePageSettings, useSharePage, useUrlState } from '@app/utils/hooks';
import { usePagesListTable } from '@app/utils/hooks/pages/usePagesListTable';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Cms } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { PageFormModal } from '../cms/PageFormModal';
import { ListFilter } from '../ListFilter';
import { Button } from '../various/Button';
import { PageTitle } from '../various/PageTitle';
import { Pagination } from '../various/Pagination';
import { SearchTopBar } from '../various/SearchTopBar';
import { Table } from '../various/Table';
import { TableControls } from '../various/TableControls';

import { PagesListCard, PagesListCardSkeleton } from './PagesListCard';
import { PagesListFallback } from './PagesListFallback';
import { SharePageModal } from './SharePageModal';

export const PagesList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['confirmationConfig', 'common', 'cms']);

  const [searchQuery, setSearchQuery] = useUrlState('search');
  const [statusFilter, setStatusFilter] = useUrlState<Cms.PageStatus>('filter');

  const { data: pages = [], isError, isLoading: isLoadingInitialQuery, isPlaceholderData } = usePages({ search: searchQuery });
  const { mutate: createPage, isPending: isCreatingPage } = useCreatePage();
  const isLoading = isLoadingInitialQuery || isPlaceholderData;

  const dataSource = React.useMemo(() => {
    const filteredPages = pages.filter(pageFilter(statusFilter));

    return [...filteredPages].sort((a, b) => (b.lastModifiedDate > a.lastModifiedDate ? 1 : -1));
  }, [pages, statusFilter]);

  React.useEffect(() => {
    dispatch(fetchMarketsRequest());
  }, [dispatch, searchQuery]);

  const { isSavingPage, setEditingPage, onEdit, selectedPageLanguages, selectedPageSettings } = usePageSettings(pages);

  const {
    isModalVisible: isCreateModalVisible,
    hideModal: hideCreateModal,
    showModal: showCreateModal,
  } = useModalVisibility('CreatePageFormModal');

  const { isModalVisible: isSettingsModalVisible, hideModal: hideSettingsModal } = useModalVisibility('EditPageFormModal');

  const { isSharePageOpen, pageToShare, openSharePage, closeSharePage } = useSharePage(pages);

  const onCreatePage = React.useCallback(
    (data: Cms.CreatePageDTO) => {
      createPage(
        { ...data, version: latestPageStructureVersion },
        {
          onSuccess: hideCreateModal,
        },
      );
    },
    [createPage, hideCreateModal],
  );

  const handlePageSettingsClosed = () => {
    setEditingPage(undefined);
  };

  const numberOfPages = dataSource.length;

  const hasNoPages = isEmpty(pages);
  const shouldShowFallback = !isLoading && (isError || hasNoPages);

  const filters = React.useMemo<{ name: string; value: Cms.PageStatus | '' }[]>(
    () => [
      {
        name: t('common:all'),
        value: '',
      },
      {
        name: t('cms:published'),
        value: 'published',
      },
      {
        name: t('cms:unpublished_changes'),
        value: 'published_with_changes',
      },
      {
        name: t('cms:unpublished'),
        value: 'unpublished',
      },
    ],
    [t],
  );

  const { mutate: duplicatePage, isPending: isDuplicatingPage } = useDuplicatePage();
  const { columnsVisibility, table, pagination } = usePagesListTable({
    duplicatePage,
    openSharePage,
    pages: dataSource,
    setEditingPage,
    statusFilter,
  });

  return (
    <PageTitle title={t('common:page_other')}>
      <div className="px-4 lg:px-8">
        <SearchTopBar
          searchPlaceholder={t('cms:search_page')}
          title={t('common:page_other')}
          totalCount={numberOfPages}
          postfix={
            <Button onClick={showCreateModal} dataTestId="createSelectionButton">
              {t('cms:create_page')}
            </Button>
          }
          search={searchQuery}
          onSearchChange={setSearchQuery}
        >
          <ListFilter title={t('cms:select_page_status')} onChange={setStatusFilter} selected={statusFilter} filters={filters} />
          <TableControls table={table} columnsConfiguration={columnsVisibility} tableName="pages" />
        </SearchTopBar>
        {shouldShowFallback ?
          <PagesListFallback hasError={isError} hasPages={hasNoPages} />
        : <>
            <Table
              table={table}
              isLoading={isLoading || isDuplicatingPage}
              renderSkeletonCard={index => <PagesListCardSkeleton key={index} />}
              renderCard={(row, index) => (
                <PagesListCard
                  key={index}
                  row={row}
                  statusFilter={statusFilter}
                  duplicatePage={duplicatePage}
                  openSharePage={openSharePage}
                  setEditingPage={setEditingPage}
                />
              )}
            />
            <Pagination
              onChange={table.setPageIndex}
              current={pagination.pageIndex + 1}
              totalItems={dataSource.length}
              manualPagination={false}
            />
          </>
        }
        <PageFormModal
          onSubmit={onCreatePage}
          title={t('cms:create_page')}
          description={t('cms:create_page_description')}
          onClose={hideCreateModal}
          isOpen={isCreateModalVisible}
          isLoading={isCreatingPage}
        />
        <PageFormModal
          defaultValues={selectedPageSettings}
          onSubmit={onEdit}
          title={t('cms:page_settings')}
          onClose={hideSettingsModal}
          onClosed={handlePageSettingsClosed}
          isOpen={isSettingsModalVisible && isDefined(selectedPageSettings)}
          isLoading={isSavingPage}
          pageLanguages={selectedPageLanguages}
        />
        {isDefined(pageToShare) && <SharePageModal isOpen={isSharePageOpen} onClose={closeSharePage} {...pageToShare} />}
      </div>
    </PageTitle>
  );
};
